import React, { useState } from 'react';

const FormInput = ({ label, type = 'text', name, value, onChange, required = false }) => (
    <div className="flex-1">
        <label htmlFor={name} className="block text-sm font-medium text-neutral-300 mb-1">
            {label}{required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent"
        />
    </div>
);

const FormTextarea = ({ label, name, value, onChange, required = false }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-neutral-300 mb-1">
            {label}{required && <span className="text-red-500">*</span>}
        </label>
        <textarea
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            rows="4"
            required={required}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent"
        ></textarea>
    </div>
);

// Modal component for displaying validation errors and success messages
const FeedbackModal = ({ title, message, onClose, isError }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#0b0e14] p-6 shadow-2xl">
            <h3 className={`text-lg font-semibold mb-3 ${isError ? 'text-red-400' : 'text-emerald-400'}`}>{title}</h3>
            <p className="text-sm text-neutral-300 mb-6">{message}</p>
            <button
                onClick={onClose}
                className={`w-full font-semibold py-2 px-4 rounded-lg transition-colors ${isError ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-cyan-500 hover:bg-cyan-400 text-black'}`}
            >
                OK
            </button>
        </div>
    </div>
);


const ContactPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        details: ''
    });

    const [modalInfo, setModalInfo] = useState({ isOpen: false, message: '', title: '', isError: false });

    const showModal = (title, message, isError = true) => {
        setModalInfo({ isOpen: true, title, message, isError });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading) return; // prevent double submission
        setIsLoading(true);

        // --- Frontend Validation ---
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.details) {
            setIsLoading(false);
            showModal('Missing Information', 'Please fill out all required fields.');
            return;
        }
        // Any valid-looking domain — a TLD allowlist wrongly rejected real
        // addresses (e.g. .vn, .dev).
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        if (!emailRegex.test(formData.email)) {
            setIsLoading(false);
            return showModal('Invalid Input', 'Please enter a valid email address.');
        }
        const phoneRegex = /^(\+1\s?)?(\([0-9]{3}\)|[0-9]{3})([\s.-]?[0-9]{3})([\s.-]?[0-9]{4})$/;
        if (!phoneRegex.test(formData.phone)) {
            setIsLoading(false);
            showModal('Invalid Phone Number', 'Please enter a valid phone number.');
            return;
        }
        const startTime = Date.now();

        try {
            const response = await fetch('https://renaisons.com/api/contact.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Server responded with ${response.status}: ${errorText}`);
            }

            const result = await response.json();

            if (result.status === 'success') {
                const elapsed = Date.now() - startTime;
                const remaining = Math.max(5000 - elapsed, 0);

                setTimeout(() => {
                    showModal('Success', 'Your inquiry has been sent successfully.', false);
                    setFormData({ firstName: '', lastName: '', email: '', phone: '', details: '' });
                    setIsLoading(false);
                }, remaining);

            } else {
                throw new Error(result.message || 'An unknown error occurred.');
            }
        } catch (error) {
            console.error("Submission Error:", error);
            showModal('Submission Error', error.message);
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-8 md:p-12">
            {isLoading && (
                <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center text-neutral-300">
                    <div className="h-8 w-8 rounded-full border-2 border-neutral-700 border-t-cyan-400 animate-spin mb-4" />
                    Submitting…
                </div>
            )}
            {modalInfo.isOpen && <FeedbackModal title={modalInfo.title} message={modalInfo.message} isError={modalInfo.isError} onClose={() => setModalInfo({ isOpen: false, message: '', title: '', isError: false })} />}

            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-neutral-400 mb-12">
                Get in touch with our team to learn more about <a href="/company/about-us" className="text-cyan-300 hover:text-cyan-200 underline hover:no-underline">Renaisons</a>.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-6 sm:p-8">
                <div className="flex flex-col md:flex-row gap-6">
                    <FormInput label="First name" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
                    <FormInput label="Last name" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
                </div>

                <div className="flex flex-col md:flex-row gap-6">
                    <FormInput label="Email Address" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
                    <FormInput label="Phone number" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} required />
                </div>

                <FormTextarea
                    label="What's your question?"
                    name="details"
                    value={formData.details}
                    onChange={handleInputChange}
                    required
                />

                <div>
                    <button
                        type="submit"
                        className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2.5 px-6 rounded-lg transition-colors disabled:opacity-50 shadow-lg shadow-cyan-500/10"
                        disabled={isLoading}
                    >
                        {isLoading ? "Submitting" : "Submit"}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ContactPage;
